import { useEffect, useState } from 'react';
import { api } from '../api';
import { styles, colors, fmtMoney, fmtMoneyK, fmtDate, useIsMobile } from './UI';

// Drill-down modal for the Sales Revenue billed (Actual) tile on the
// dashboard. Shows two views over the same scope:
//   1. Customer rollup -- sum of amount per customer, ordered desc
//   2. Raw rows        -- the underlying Rentalman invoice lines
//
// Filters are passed in via props (kind, year, branch_id, owner_id) so the
// drill-down honors whatever the dashboard tile reflected. Same scope =
// numbers reconcile to the dollar.
export default function BilledDrillModal({ kind, year, branchId, ownerId, scopeLabel, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('rollup'); // 'rollup' | 'raw'
  const [search, setSearch] = useState('');
  const isMobile = useIsMobile();

  useEffect(() => {
    setError(''); setData(null);
    const params = { kind };
    if (year) params.year = year;
    if (branchId) params.branch_id = branchId;
    if (ownerId) params.owner_id = ownerId;
    api.billedDrill(params).then(setData).catch(e => setError(e.message));
  }, [kind, year, branchId, ownerId]);

  // Client-side search across customer name in both tabs.
  const q = search.trim().toLowerCase();
  const rollupFiltered = data?.rollup.filter(r =>
    !q || (r.customer_name || '').toLowerCase().includes(q)
  ) || [];
  const rawFiltered = data?.raw.filter(r =>
    !q || (r.customer_name || '').toLowerCase().includes(q)
       || (r.description || '').toLowerCase().includes(q)
       || (r.inv_no || '').toLowerCase().includes(q)
  ) || [];

  const tileLabel = kind === 'Base' ? 'Base Billed (Actual)'
                   : kind === 'TA'  ? 'TA Billed (Actual) · Project mode'
                   : 'All Billed (Actual)';

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20, zIndex: 1000
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 10, width: '100%', maxWidth: 1100,
          maxHeight: '90vh',
          // Mobile: the whole modal scrolls (pinned header + tabs starve the
          // body of scroll room on zoomed phones — same fix as the Job Hub).
          ...(isMobile
            ? { overflowY: 'auto', WebkitOverflowScrolling: 'touch' }
            : { display: 'flex', flexDirection: 'column' }),
          boxShadow: '0 20px 50px rgba(0,0,0,0.3)'
        }}
      >
        {/* Header */}
        <div style={{ padding: '18px 22px', borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 style={{ ...styles.h2, margin: 0, fontSize: 18 }}>
              {tileLabel}
              {scopeLabel && <span style={{ color: colors.textMuted, fontWeight: 400, fontSize: 13, marginLeft: 8 }}>· {scopeLabel}</span>}
            </h2>
            {data && (
              <div style={{ fontSize: 13, color: colors.textMuted, marginTop: 4 }}>
                <strong style={{ color: colors.charcoal, fontSize: 16 }}>{fmtMoney(data.total)}</strong>
                {' · '}{Number(data.row_count).toLocaleString()} invoice line{data.row_count === 1 ? '' : 's'}
                {' · '}{rollupFiltered.length} customer{rollupFiltered.length === 1 ? '' : 's'}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', fontSize: 24, cursor: 'pointer', color: colors.textMuted }}
            aria-label="Close"
          >×</button>
        </div>

        {/* Tabs + search */}
        <div style={{ padding: '12px 22px', borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'inline-flex', borderRadius: 6, overflow: 'hidden', border: `1px solid ${colors.border}` }}>
            {[
              { v: 'rollup', label: '👥 By Customer' },
              { v: 'raw',    label: '📄 Raw Invoice Rows' }
            ].map(t => {
              const active = tab === t.v;
              return (
                <button
                  key={t.v}
                  type="button"
                  onClick={() => setTab(t.v)}
                  style={{
                    padding: '6px 14px', border: 'none', cursor: 'pointer',
                    fontSize: 13, fontWeight: 600,
                    background: active ? colors.charcoal : '#fff',
                    color: active ? '#fff' : colors.charcoal
                  }}
                >{t.label}</button>
              );
            })}
          </div>
          <input
            type="text"
            placeholder={tab === 'rollup' ? 'Search customer…' : 'Search customer / description / invoice #…'}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ ...styles.input, flex: 1, minWidth: 200, maxWidth: 400 }}
          />
        </div>

        {/* Body (own scroll on desktop; on mobile the whole modal scrolls,
            keeping overflowX for the wide raw-rows table) */}
        <div style={{ padding: '0 22px 22px', ...(isMobile ? { overflowX: 'auto' } : { overflow: 'auto', flex: 1 }) }}>
          {error && <div style={{ ...styles.errorBox, marginTop: 16 }}>{error}</div>}
          {!data && !error && <div style={{ padding: 40, textAlign: 'center', color: colors.textMuted }}>Loading drill-down…</div>}

          {data && tab === 'rollup' && (
            <div style={{ paddingTop: 12 }}>
              {!rollupFiltered.length ? (
                <div style={{ padding: 40, textAlign: 'center', color: colors.textMuted }}>
                  {q ? (
                    'No customers match your search.'
                  ) : data.row_count === 0 ? (
                    <>
                      <div style={{ fontSize: 14, fontWeight: 600, color: colors.text, marginBottom: 8 }}>
                        Drill-down archive is empty for this scope.
                      </div>
                      <div style={{ fontSize: 13, lineHeight: 1.5 }}>
                        The dashboard total comes from monthly aggregates, but the per-customer drill-down
                        needs the raw invoice archive (introduced 2026-05-14). To populate it, go to{' '}
                        <strong>Admin → Sales Revenue → ⬆ Upload Rentalman File</strong> and re-upload the
                        same months — the importer wipes and re-inserts both the aggregates and the
                        per-row archive in one transaction. After commit, this drill-down will populate.
                      </div>
                    </>
                  ) : (
                    'No customers in this scope yet.'
                  )}
                </div>
              ) : (
                <table style={{ ...styles.table, fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Customer</th>
                      <th style={styles.th}>Cust #</th>
                      <th style={{ ...styles.th, textAlign: 'right' }}>Invoice Lines</th>
                      <th style={{ ...styles.th, textAlign: 'right' }}>Total</th>
                      <th style={{ ...styles.th, textAlign: 'right' }}>% of Scope</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rollupFiltered.map((r, i) => {
                      const pct = data.total > 0 ? (Number(r.total) / data.total) * 100 : 0;
                      return (
                        <tr key={(r.customer_no || '') + ':' + r.customer_name + ':' + i}>
                          <td style={{ ...styles.td, fontWeight: 600 }}>{r.customer_name || '—'}</td>
                          <td style={{ ...styles.td, color: colors.textMuted }}>{r.customer_no || '—'}</td>
                          <td style={{ ...styles.td, textAlign: 'right' }}>{Number(r.row_count).toLocaleString()}</td>
                          <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700 }}>{fmtMoneyK(r.total)}</td>
                          <td style={{ ...styles.td, textAlign: 'right', color: colors.textMuted }}>{pct.toFixed(1)}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {data && tab === 'raw' && (
            <div style={{ paddingTop: 12 }}>
              {data.row_count > data.raw_limit && (
                <div style={{ background: '#FEF3C7', border: '1px solid #FBBF24', color: '#92400E', padding: 8, borderRadius: 6, marginBottom: 8, fontSize: 12 }}>
                  Showing newest <strong>{data.raw_limit.toLocaleString()}</strong> of <strong>{data.row_count.toLocaleString()}</strong> invoice lines.
                  Use the search box to narrow, or switch to By Customer for the full picture.
                </div>
              )}
              {!rawFiltered.length ? (
                <div style={{ padding: 40, textAlign: 'center', color: colors.textMuted }}>
                  {q ? (
                    'No invoice lines match your search.'
                  ) : data.row_count === 0 ? (
                    <>
                      <div style={{ fontSize: 14, fontWeight: 600, color: colors.text, marginBottom: 8 }}>
                        Drill-down archive is empty for this scope.
                      </div>
                      <div style={{ fontSize: 13, lineHeight: 1.5 }}>
                        Re-upload the Rentalman file in <strong>Admin → Sales Revenue</strong> to populate
                        the per-row archive. (The dashboard totals are unaffected — they read from a
                        separate aggregate table.)
                      </div>
                    </>
                  ) : (
                    'No invoice lines in this scope yet.'
                  )}
                </div>
              ) : (
                <table style={{ ...styles.table, fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Inv Date</th>
                      <th style={styles.th}>Customer</th>
                      <th style={styles.th}>Inv #</th>
                      <th style={styles.th}>Loc</th>
                      <th style={styles.th}>SQ</th>
                      <th style={styles.th}>Description</th>
                      <th style={{ ...styles.th, textAlign: 'right' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rawFiltered.map(r => (
                      <tr key={r.id}>
                        <td style={styles.td}>{fmtDate(r.inv_date)}</td>
                        <td style={{ ...styles.td, fontWeight: 600 }}>{r.customer_name || '—'}</td>
                        <td style={{ ...styles.td, color: colors.textMuted }}>{r.inv_no || '—'}</td>
                        <td style={{ ...styles.td, color: colors.textMuted }}>{r.loc || '—'}</td>
                        <td style={styles.td}>
                          {r.sq}
                          {r.reclassified && (
                            <span title="Reclassified Consumables → Rentals based on description keyword" style={{ marginLeft: 4, fontSize: 9, color: '#7C3AED', fontWeight: 700 }}>
                              →R
                            </span>
                          )}
                        </td>
                        <td style={{ ...styles.td, fontSize: 11, color: colors.textMuted, maxWidth: 320, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.description || ''}>
                          {r.description || '—'}
                        </td>
                        <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600 }}>{fmtMoney(r.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
